import { Link } from 'react-router-dom'
import {
  Code2,
  ExternalLink,
  KeyRound,
  Plus,
  Puzzle,
  Webhook,
} from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { ChannelAvatar } from '@/components/ChannelAvatar'
import { useStore } from '@/lib/store'
import { useUI } from '@/lib/ui'
import { PLATFORMS } from '@/lib/platforms'
import { DisconnectChannelButton } from '@/features/channels/DisconnectChannelButton'

const INTEGRATIONS = [
  {
    id: 'mcp',
    name: 'MCP server',
    description: 'Connect Claude, ChatGPT, Cursor, VS Code, Codex or another MCP client.',
    icon: Code2,
    href: '/docs/mcp',
    action: 'Open setup guide',
  },
  {
    id: 'api-keys',
    name: 'API keys',
    description: 'Create keys for automation tools and headless MCP clients.',
    icon: KeyRound,
    href: '/settings?tab=Developers',
    action: 'Manage keys',
  },
  {
    id: 'webhooks',
    name: 'Webhooks',
    description: 'Send signed HTTP callbacks for publish, failure, schedule and channel events.',
    icon: Webhook,
    href: '/settings?tab=Webhooks',
    action: 'Manage webhooks',
  },
]

export function PlugsPage() {
  const channels = useStore((s) => s.channels)
  const openConnect = useUI((s) => s.openConnect)

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Channels"
        subtitle="Connected accounts and real integration paths"
        icon={<Puzzle size={20} />}
        actions={
          <button type="button" className="btn-primary" onClick={openConnect}>
            <Plus size={16} />
            Connect channel
          </button>
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-8">
        <section>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-ink-900">Social accounts</h2>
              <p className="mt-1 text-sm text-ink-500">
                These are the accounts Hey Tasky can schedule, publish and analyze.
              </p>
            </div>
            <span className="chip border border-line bg-panel text-ink-600">
              {channels.length} connected
            </span>
          </div>

          {channels.length === 0 ? (
            <div className="card flex flex-wrap items-center justify-between gap-4 p-5">
              <div>
                <p className="font-display text-base font-semibold text-ink-900">
                  No social accounts connected
                </p>
                <p className="mt-1 max-w-md text-sm text-ink-500">
                  Add a live channel before scheduling posts, collecting replies or showing
                  analytics.
                </p>
              </div>
              <button type="button" className="btn-primary" onClick={openConnect}>
                <Plus size={16} />
                Connect channel
              </button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {channels.map((channel) => {
                const platform = PLATFORMS[channel.platform]
                return (
                  <div key={channel.id} className="card flex flex-col gap-3 p-4">
                    <div className="flex items-start gap-3">
                      <ChannelAvatar channel={channel} size="md" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-display text-base font-semibold text-ink-900">
                          {channel.name}
                        </p>
                        <p className="truncate text-sm text-ink-500">{channel.handle}</p>
                        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-line bg-panel px-2 py-0.5 text-xs text-ink-500">
                          <span
                            className="grid h-3.5 w-3.5 place-items-center"
                            style={{ color: platform.color === '#ffffff' ? '#111' : platform.color }}
                          >
                            <span className="inline-flex h-3.5 w-3.5">{platform.icon}</span>
                          </span>
                          {platform.name}
                        </p>
                      </div>
                    </div>
                    <DisconnectChannelButton channel={channel} />
                  </div>
                )
              })}
            </div>
          )}
        </section>

        <section>
          <div className="mb-3">
            <h2 className="font-display text-lg font-bold text-ink-900">Integrations</h2>
            <p className="mt-1 text-sm text-ink-500">
              These are the integration surfaces wired in this product today.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {INTEGRATIONS.map((integration) => {
              const Icon = integration.icon
              return (
                <div key={integration.id} className="card flex flex-col gap-4 p-5">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-50 text-brand-500">
                    <Icon size={20} />
                  </span>
                  <div>
                    <h3 className="font-display font-semibold text-ink-900">{integration.name}</h3>
                    <p className="mt-1 text-sm text-ink-500">{integration.description}</p>
                  </div>
                  <Link
                    to={integration.href}
                    className="btn-ghost mt-auto w-full justify-center"
                  >
                    {integration.action}
                    <ExternalLink size={15} />
                  </Link>
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}
